/**
 * 広告同意管理ユーティリティ
 *
 * - ログイン済み: varuna DB（ad_consent_flags）で管理 → クロスデバイス同期
 * - 非ログイン: Cookie にフォールバック（端末ごと）
 *
 * Cookie キー形式: _YAGISH_AD_CONSENT_{NETWORK_ID}
 * DB 形式: ad_consent_flags = '{"meta":"1","tiktok":"0"}'
 */

import { AD_NETWORKS, AD_TAGS, AD_FIRING_ENABLED, AD_RECORDING_ENABLED, isTagEnabled } from '@/config/ad-networks'

// ── Cookie ───────────────────────────────────────────────────────────

function cookieKey (networkId) {
  return `_YAGISH_AD_CONSENT_${networkId.toUpperCase()}`
}

function getCookie (key) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + encodeURIComponent(key) + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

function setCookie (key, value) {
  const expires = new Date()
  expires.setFullYear(expires.getFullYear() + 1)
  document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`
}

// ── ローカル判定（Cookie） ───────────────────────────────────────────

/** Cookie から同意フラグを取得（'1' | '0' | null） */
export function getConsentFlag (networkId) {
  return getCookie(cookieKey(networkId))
}

/** Cookie に同意フラグを保存 */
export function setConsentFlag (networkId, value) {
  setCookie(cookieKey(networkId), value)
}

/** 同意済みかどうか */
export function hasConsent (networkId) {
  return getConsentFlag(networkId) === '1'
}

/** 同意・非同意いずれかを選択済みか */
export function isConsentDecided (networkId) {
  return getConsentFlag(networkId) !== null
}

/** 未決定のネットワーク一覧を返す */
export function getUndecidedNetworks () {
  return AD_NETWORKS.filter(n => !isConsentDecided(n.id))
}

// ── モーダル再表示の制御 ──────────────────────────────────────────────
// 非同意者（未決定 or 非同意）には最大 AD_PROMPT_MAX 回まで表示する。
// 内訳：初回1回 ＋ 再訪時2回 ＝ 計3回。同意済み（'1'）には表示しない。
// 表示回数は Cookie（端末単位）で管理。

export const AD_PROMPT_MAX = 3
const PROMPT_COUNT_KEY = '_YAGISH_AD_PROMPT_COUNT'

/** これまでのモーダル表示回数（Cookie・端末単位） */
export function getPromptCount () {
  return parseInt(getCookie(PROMPT_COUNT_KEY) || '0', 10) || 0
}

/** 表示回数を1増やす */
export function incrementPromptCount () {
  setCookie(PROMPT_COUNT_KEY, String(getPromptCount() + 1))
}

/** 未同意（'1'以外＝未決定 null / 非同意 '0'）のネットワーク一覧。非同意者への再表示に使う */
export function getPromptableNetworks () {
  return AD_NETWORKS.filter(n => getConsentFlag(n.id) !== '1')
}

// ── varuna API 連携 ──────────────────────────────────────────────────

/**
 * varuna から全ネットワークの同意フラグを取得し Cookie に同期する。
 * @returns {Promise<object>} { meta: '1', tiktok: '0', ... }
 */
export async function fetchAllFlagsFromServer (axios, varuna, uuid) {
  try {
    const formData = new FormData()
    formData.append('uuid', uuid)
    const res = await axios.post(varuna.endPoints.getAdConsentFlags, formData, { timeout: 10000 })
    const flags = res.data.flags || {}
    // Cookie に同期
    for (const [id, val] of Object.entries(flags)) {
      if (val === '1' || val === '0') {
        setCookie(cookieKey(id), val)
      }
    }
    return flags
  } catch (e) {
    return {}
  }
}

/**
 * 特定ネットワークの同意フラグを varuna と Cookie 両方に保存する。
 * @param {string} networkId
 * @param {boolean} value
 */
export async function saveConsentFlagToServer (axios, varuna, uuid, networkId, value) {
  setConsentFlag(networkId, value ? '1' : '0')
  try {
    const formData = new FormData()
    formData.append('uuid', uuid)
    formData.append('network_id', networkId)
    formData.append('consent_value', value ? '1' : '0')
    await axios.post(varuna.endPoints.updateAdConsentFlag, formData, { timeout: 10000 })
  } catch (e) {
    // サーバー保存失敗は Cookie のみで継続（サイレント）
  }
}

// ── タグ発火 ─────────────────────────────────────────────────────────

/** 指定した同意グループ(consentId)に紐づく全タグを発火（発火有効・同意済み・タグ有効時のみ） */
export function fireNetwork (consentId) {
  if (!AD_FIRING_ENABLED) return
  if (!hasConsent(consentId)) return
  for (const tag of AD_TAGS) {
    if (tag.consentId === consentId && isTagEnabled(tag.id) && typeof tag.fire === 'function') tag.fire()
  }
}

/** 指定した同意グループに紐づくタグの Lead(CV) イベントを発火 */
export function fireLeadEvent (consentId) {
  if (!AD_FIRING_ENABLED) return
  if (!hasConsent(consentId)) return
  for (const tag of AD_TAGS) {
    if (tag.consentId === consentId && isTagEnabled(tag.id) && typeof tag.fireLead === 'function') tag.fireLead()
  }
}

/** 同意済み全ネットワークのタグを一括発火（ページ遷移時など） */
export function fireAllConsented () {
  for (const network of AD_NETWORKS) {
    fireNetwork(network.id)
  }
}

/** 同意済み全ネットワークの Lead CV イベントを一括発火 */
export function fireAllLeadEvents () {
  for (const network of AD_NETWORKS) {
    fireLeadEvent(network.id)
  }
}

// ── 個別タグの記録状態（ad_tags_state） ──────────────────────────────
// 「どのタグを（発火対象として）記録したか」を端末Cookie＋DBで管理。同意とは別。

function tagCookieKey (tagId) {
  return `_YAGISH_AD_TAG_${tagId.toUpperCase()}`
}

/** タグが記録済みか（Cookie・端末単位） */
export function isTagRecorded (tagId) {
  return getCookie(tagCookieKey(tagId)) === '1'
}

function recordTagLocal (tagId) {
  setCookie(tagCookieKey(tagId), '1')
}

/** タグ記録状態を varuna に保存（ログイン時のみ）。成功時 true を返す */
export async function saveTagStateToServer (axios, varuna, uuid, tagId) {
  try {
    const formData = new FormData()
    formData.append('uuid', uuid)
    formData.append('tag_id', tagId)
    const res = await axios.post(varuna.endPoints.updateAdTagsState, formData, { timeout: 10000 })
    return !!(res && res.data && res.data.result === '1')
  } catch (e) {
    return false
  }
}

/**
 * ページ表示ごとに呼ぶ（router afterEach から）。同意済み・有効なタグに対して：
 *  - 発火する（AD_FIRING_ENABLED 時・全ページ）
 *  - 実際に発火したタグだけを記録する（AD_RECORDING_ENABLED 時：Cookie＋DB・初回のみ）
 * ＝ ad_tags_state は「実際に発火したタグ」を表す。発火しなければ記録もされない。
 * @param {object} ctx - { axios, varuna, firebase }
 */
export function processAdTags (ctx) {
  const authed = !!(ctx && ctx.firebase && ctx.firebase.isAuthenticated)
  const uuid = authed ? ctx.firebase.user.uid : null
  for (const tag of AD_TAGS) {
    if (!isTagEnabled(tag.id)) continue
    if (!hasConsent(tag.consentId)) continue
    // 発火しない場合は記録もしない（＝「実際に発火したタグ」だけを ad_tags_state に残す）
    if (!AD_FIRING_ENABLED || typeof tag.fire !== 'function') continue
    // 実際に発火する（全ページ）
    tag.fire()
    // 発火したので記録する（初回のみ・記録フラグON時）
    if (AD_RECORDING_ENABLED && !isTagRecorded(tag.id)) {
      if (authed && ctx.axios && ctx.varuna) {
        // ログイン: DB保存が成功した時だけ記録済みCookieを立てる（失敗時は次ページで再試行）
        saveTagStateToServer(ctx.axios, ctx.varuna, uuid, tag.id).then(ok => {
          if (ok) recordTagLocal(tag.id)
        })
      } else {
        // 非ログイン: DB(member)に持てないため Cookie のみ
        recordTagLocal(tag.id)
      }
    }
  }
}
